// src/pages/app/TournamentDates.js
import React, { useEffect, useMemo, useState } from "react";
import { db } from "../../lib/firebase";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import useUserRole, { normalizeRole } from "../../hooks/useUserRole";
import AdminPageHeader from "../../components/AdminPageHeader";

const DOC_PATH = ["appConfig", "tournamentWeek"];

function isISODate(s) {
  return typeof s === "string" && /^\d{4}-\d{2}-\d{2}$/.test(s);
}

function addDaysISO(iso, days) {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + days);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export default function TournamentDates() {
  const { role, loading: roleLoading } = useUserRole();
  const roleNorm = useMemo(() => normalizeRole(role), [role]);

  const canEdit =
    roleNorm === "admin" ||
    roleNorm === "super user" ||
    roleNorm === "super admin";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const ref = doc(db, ...DOC_PATH);
        const snap = await getDoc(ref);

        if (cancelled) return;

        if (snap.exists()) {
          const data = snap.data() || {};
          setStartDate(isISODate(data.startDate) ? data.startDate : "");
          setEndDate(isISODate(data.endDate) ? data.endDate : "");
        }
      } catch (e) {
        console.error(e);
        if (!cancelled) setErr("Failed to load tournament dates.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const computedEnd = useMemo(() => {
    if (isISODate(startDate) && !isISODate(endDate)) {
      return addDaysISO(startDate, 6);
    }
    return "";
  }, [startDate, endDate]);

  const effectiveEndDate = endDate || computedEnd;

  async function handleSaveDates() {
    setErr("");

    if (!canEdit) {
      setErr("You do not have permission to edit tournament dates.");
      return;
    }

    if (!isISODate(startDate)) {
      setErr("Start date is required.");
      return;
    }

    const finalEnd = effectiveEndDate;

    try {
      setSaving(true);

      const ref = doc(db, ...DOC_PATH);

      await setDoc(
        ref,
        {
          startDate,
          endDate: finalEnd,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      setEndDate(finalEnd);
    } catch (e) {
      console.error(e);
      setErr("Failed to save tournament dates.");
    } finally {
      setSaving(false);
    }
  }

  if (roleLoading) return <div style={{ padding: 16 }}>Loading…</div>;
  if (!canEdit) return <div style={{ padding: 16 }}>You do not have access to this page.</div>;

  return (
    <div style={{ padding: 16, maxWidth: 900 }}>
      <AdminPageHeader title="Set Tournament Dates" level="h2" />

      <p style={{ color: "#64748b", marginTop: 4 }}>
        These dates control what week the schedule displays.
      </p>

      {err && (
        <div style={{ marginTop: 12, padding: 12, borderRadius: 12, background: "#fff1f2", color: "#991b1b" }}>
          {err}
        </div>
      )}

      {loading ? (
        <div style={{ padding: 12 }}>Loading…</div>
      ) : (
        <div style={{ marginTop: 14, padding: 14, border: "1px solid #e2e8f0", borderRadius: 14, background: "#fff" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <div style={{ fontWeight: 900, marginBottom: 6 }}>Start (Monday)</div>
              <input
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  if (!isISODate(endDate)) setEndDate("");
                }}
                style={{
                  height: 40,
                  borderRadius: 12,
                  border: "1px solid #cbd5e1",
                  padding: "0 12px",
                  width: "100%",
                }}
              />
            </div>

            <div>
              <div style={{ fontWeight: 900, marginBottom: 6 }}>End (Sunday)</div>
              <input
                type="date"
                value={effectiveEndDate}
                onChange={(e) => setEndDate(e.target.value)}
                style={{
                  height: 40,
                  borderRadius: 12,
                  border: "1px solid #cbd5e1",
                  padding: "0 12px",
                  width: "100%",
                }}
              />

              {!endDate && computedEnd && (
                <div style={{ color: "#64748b", fontSize: 12, marginTop: 6 }}>
                  Auto end: {computedEnd}
                </div>
              )}
            </div>
          </div>

          <div style={{ marginTop: 14 }}>
            <button className="btn btn-primary" disabled={saving} onClick={handleSaveDates}>
              {saving ? "Saving…" : "Save Tournament Dates"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}